import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchResourceFiles } from 'js/actions';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { useResourceStatsFiles } from 'new/hooks/adapters/useResourceStatsFiles';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import { stripEmptyValues } from 'new/utils';
import {
  sortByItemsRead,
  sortByItemsWrite,
  getFilterByStatusRead,
  getFilterByStatusWrite,
  allowedTypes,
  getDefaultSort,
} from 'new/elements/ReadStats/constants';

export default function useFileItems(
  resourceId,
  resourceType,
  requestOnChange = false,
  isAdaptiveFlow = false,
) {
  const dispatch = useAppDispatch();
  const { meta } = useResourceStatsFiles(resourceType, resourceId);

  const sortByItems = resourceType === RESOURCE_TYPE_SOURCE ? sortByItemsRead : sortByItemsWrite;

  const filterByStatus =
    resourceType === RESOURCE_TYPE_SOURCE
      ? getFilterByStatusRead(isAdaptiveFlow)
      : getFilterByStatusWrite(isAdaptiveFlow);

  const fileNameRef = useRef('');
  const [sort, setSort] = useState(meta.orderBy || getDefaultSort(resourceType));
  const [status, setStatus] = useState(meta.status || filterByStatus[0].value);

  useEffect(() => {
    // if resource type changes it means we should request files as default
    if (requestOnChange && allowedTypes.includes(resourceType)) {
      dispatch(
        fetchResourceFiles(
          resourceId,
          resourceType,
          {
            page: 1,
            size: 50,
            orderby: getDefaultSort(resourceType),
            status: '',
          },
          null,
        ),
      );
    }
  }, [resourceType, requestOnChange]);

  const dispatchFetchFiles = useCallback(
    ({ resourceId, resourceType, page, orderby, status, name = '' }) => {
      const resolvedFileName = name || fileNameRef.current || '';

      const filters = stripEmptyValues({
        page,
        size: 50,
        orderby,
        status,
        fileName: resolvedFileName, //TODO: temp: use fileName instead of file_name
      });

      dispatch(fetchResourceFiles(resourceId, resourceType, filters));
    },
    [dispatch],
  );

  const onChangeSort = useCallback(
    (e) => {
      const newSort = e.target.value;
      setSort(newSort);
      dispatchFetchFiles({
        page: 1,
        orderby: newSort,
        status: status === 'all' ? '' : status,
        resourceType,
        resourceId,
      });
    },
    [status, resourceType, resourceId, dispatchFetchFiles],
  );

  const onChangeStatus = useCallback(
    (e) => {
      const newStatus = e.target.value;
      const requestStatus = newStatus === 'all' ? '' : newStatus;
      setStatus(newStatus);
      dispatchFetchFiles({
        page: 1,
        orderby: sort,
        status: requestStatus,
        resourceType,
        resourceId,
      });
    },
    [sort, resourceType, resourceId, dispatchFetchFiles],
  );

  const onSearchByNameChange = useDebouncedCallback(
    (name: string) => {
      fileNameRef.current = name;
      dispatchFetchFiles({
        page: 1,
        orderby: sort,
        status: status === 'all' ? '' : status,
        resourceType,
        resourceId,
        name,
      });
    },
    [sort, status, resourceType, resourceId, dispatchFetchFiles],
    300,
  );

  const requestFiles = useCallback(
    (page) => {
      dispatchFetchFiles({
        page,
        resourceId,
        resourceType,
        orderby: sort,
        status: status === 'all' ? '' : status,
      });
    },
    [resourceId, resourceType, sort, status, dispatchFetchFiles],
  );

  return {
    requestFiles,
    sort,
    status,
    sortByItems,
    filterByStatus,
    onChangeSort,
    onChangeStatus,
    onSearchByNameChange,
  };
}
